import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { finalize } from 'rxjs';
import { ProductCategoryCostEntity } from '../product-category-cost.model';
import { ProductCategoryCostService } from '../product-category-cost.service';

@Component({
    selector: 'app-product-category-cost-select',
    imports: [FormsModule, Select],
    templateUrl: './product-category-cost-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductCategoryCostSelectComponent {
    private destroyRef = inject(DestroyRef);
    private productCategoryCostService = inject(ProductCategoryCostService);

    selectedProductCategoryCostKey = model<number | null>();

    id = input<string>('productCategoryCostSelect');
    placeHolder = input<string | undefined>('Select product cost category');

    subscriberKey = input.required<number | null>();

    invalid = input<boolean>(false);
    disabled = input<boolean>(false);

    options = signal<ProductCategoryCostEntity[]>([]);
    isLoading = signal<boolean>(false);
    loadingChange = output<boolean>();

    blur = output();

    isDisabled = computed(() => this.isLoading() || this.disabled());
    isInvalid = computed(() => this.invalid() && !this.isDisabled());

    constructor() {
        /* Fetch Options when Input Changes */
        effect(() => {
            const subscriberKey = this.subscriberKey();

            if (subscriberKey === null) {
                this.options.set([]);
            } else {
                this.loadOptions(subscriberKey);
            }
        });

        /* Loading Change */
        effect(() => {
            this.loadingChange.emit(this.isLoading());
        });
    }

    loadOptions(subscriberKey: number) {
        this.isLoading.set(true);
        this.options.set([]);

        this.productCategoryCostService
            .list(subscriberKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe((reportCategories) => {
                const sorted = reportCategories.sort((a, b) => a.productCategoryCostName.localeCompare(b.productCategoryCostName));
                this.options.set(sorted);
            });
    }

    onModelChange(productCategoryCostKey: number | null) {
        this.selectedProductCategoryCostKey.set(productCategoryCostKey);
    }
}
